import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Settings } from 'src/app/utils/constant/settings';
import { environment } from "src/environments/environment";
import { DialogService } from '../dialog/dialog.service';
import { LanguageService } from '../language/language.service';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root'
})
export class IdleTimeoutService implements OnDestroy {
  private idleSubscription: Subscription;
  private activitySubscription: Subscription;

  constructor(
    private authService: AuthService,
    private ngZone: NgZone,
    private dialogService: DialogService,
    private languageService: LanguageService,
    private modalDialog: MatDialog
  ) { }

  private logout(): void {
    // Close all open dialogs
    this.modalDialog.closeAll();
    this.authService.logout();
  }

  startWatching(): void {
    this.stopWatching();

    const activityEvents$ = merge(
      fromEvent(window, 'keydown'),
      fromEvent(window, 'scroll'),
      fromEvent(window, 'click'),
      fromEvent(window, 'touchstart')
    ).pipe(debounceTime(300));

    // Only user activity is outside Angular zone
    this.ngZone.runOutsideAngular(() => {
      this.activitySubscription = activityEvents$.subscribe(() => {
        this.ngZone.run(() => this.resetIdleTimer());
      });
    });

    this.resetIdleTimer();
  }

  private async resetIdleTimer(): Promise<void> {    
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }

    if (this.authService.isSessionExpired()) {
      if (environment.showLogs) {
        console.log("User's session has expired, logging out...");
      }
      this.logout();
      return;
    }

    const storedSettings = localStorage.getItem('settings');
    const settings: Settings = storedSettings
      ? JSON.parse(storedSettings)
      : { IDLE_TIMEOUT_IN_SECONDS: 300 };

    // Update session timestamp
    localStorage.setItem('lastActivityTimestamp', new Date().toISOString());

    this.idleSubscription = timer(settings.IDLE_TIMEOUT_IN_SECONDS * 1000).subscribe(() => {
      if (environment.showLogs) {
        console.log('User inactive too long, logging out...');
      }
      this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_SESSION_TIMEOUT", {}))
      this.logout();
    });
  }

  stopWatching(): void {
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this.stopWatching();
  }
}
